import { useState } from "react";
import { Alert, Button, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import type { Promisable } from "type-fest";
import z from "zod";

import { triggerBlobDownload } from "@thalamos/frontend";

import guestApi from "../../api/guest/api.js";
import type { PdfDownloadOutcome } from "../Assessment/assessmentPdfDownload.js";
import { GuestBrandedPage } from "./GuestAssessmentLayout.js";
import type { SentState } from "./GuestAssessmentOverview.js";

const SentStateSchema = z.object({
  askerName: z.string(),
}) satisfies z.ZodType<SentState>;

/**
 * Who the assessment went to, from the navigation that brought the guest
 * here, which anything could have put there.
 */
const askerNameIn = (state: unknown): string | undefined =>
  SentStateSchema.safeParse(state).data?.askerName;

const downloadTheCopy = async (): Promise<PdfDownloadOutcome> => {
  const copy = await guestApi.guest.sentPdf().catch(() => undefined);

  if (copy === undefined) {
    return "failed";
  }

  triggerBlobDownload(copy);
  return "downloaded";
};

/** The thank-you page a guest lands on once their assessment is sent. */
export const GuestSentPage = () => {
  const location: { state: unknown } = useLocation();

  return (
    <GuestSentContent
      askerName={askerNameIn(location.state)}
      onDownload={downloadTheCopy}
    />
  );
};

/**
 * The thank-you page's rendering. Sending ended the guest's access, so it
 * reads nothing from the server; the session that sent the assessment may
 * still download a copy of it, which is offered here only straight after
 * sending.
 */
export const GuestSentContent = ({
  askerName,
  onDownload,
}: {
  /** Undefined where the page was reached other than by sending. */
  askerName: string | undefined;
  onDownload: () => Promisable<PdfDownloadOutcome>;
}) => {
  const { t } = useTranslation();
  const [downloading, setDownloading] = useState(false);
  const [failed, setFailed] = useState(false);

  return (
    <GuestBrandedPage>
      <Typography variant="h4" component="h1">
        {t("guestSent.heading")}
      </Typography>
      <Typography>
        {askerName === undefined
          ? t("guestSent.sent")
          : t("guestSent.sentTo", { asker: askerName })}
      </Typography>
      {askerName === undefined ? null : (
        <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
          <Button
            variant="contained"
            disabled={downloading}
            onClick={() => {
              setDownloading(true);
              setFailed(false);
              void Promise.resolve()
                .then(onDownload)
                .catch((): PdfDownloadOutcome => "failed")
                .then((outcome) => {
                  setDownloading(false);
                  setFailed(outcome === "failed");
                });
            }}
          >
            {t("guestSent.download")}
          </Button>
          <Typography variant="body2">{t("guestSent.downloadOnce")}</Typography>
          {failed ? (
            <Alert severity="error">{t("guestSent.downloadFailed")}</Alert>
          ) : null}
        </Stack>
      )}
      <Typography>{t("guestSent.closed")}</Typography>
    </GuestBrandedPage>
  );
};
